import React, { useEffect, useRef, useState } from "react";
import uiConfig from '../../data/uiConfig';
import { ButtonVariant } from "../../utils/utils";
import { Button } from "./Button";
import clsx from "clsx";
import { Title } from "./Title";

type DialogProps = {
  children?: React.ReactNode;
  open: boolean;
  onClose: () => void;
  title?: string;
  message?: string;
  confirmLabel?: string;
  confirmVariant?: ButtonVariant;
  onConfirm?: () => void;
  noConfirmBtn?: boolean;
  customBtn?: boolean;
  width?: number;
};

export const Dialog = ({
  children,
  open,
  onClose,
  title,
  message,
  confirmLabel = "OK",
  confirmVariant = uiConfig.dialog.primaryBtn as ButtonVariant,
  onConfirm,
  noConfirmBtn,
  customBtn = false,
  width = uiConfig.dialog.width
}: DialogProps) => {

  const txtColor = "";
  const bgColor = uiConfig.dialog.bg;
  const secondaryVariant = uiConfig.dialog.secondaryBtn as ButtonVariant;
  const dialogRef = useRef<HTMLDivElement>(null);
	const [show, setShow] = useState(open);
  const [closing, setClosing] = useState(false);

  // Stabilise la référence de onClose pour éviter de relancer l'effet au moindre re-render
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Synchroniser visibilité et gérer la fermeture animée
  useEffect(() => {
    if (open) {
	    setShow(true);
      setClosing(false);
      return;
    }
    // Déclenche l'animation de sortie et on démontera après animationend
    if (show) {
      setClosing(true);
    } else {
      // Si déjà invisible, s'assurer que le body est débloqué
      document.body.style.overflow = "";
    }
  }, [open, show]);

  // Attendre la fin de l'animation de sortie pour démonter
  useEffect(() => {
    if (!closing) return;
    const node = dialogRef.current;
    if (!node) return;
    const handleAnimationEnd = () => {
	    setShow(false);
      setClosing(false);
    };
    node.addEventListener('animationend', handleAnimationEnd);
    return () => {
      node.removeEventListener('animationend', handleAnimationEnd);
    };
  }, [closing]);

  useEffect(() => {
    if (!show) {
      document.body.style.overflow = "";
      return;
    }

    // Focus trap
    const dialog = dialogRef.current;
    const focusable = dialog?.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable?.[0];
    const last = focusable?.[focusable.length - 1];

    const trap = (e: KeyboardEvent) => {
      if (e.key === "Tab") {
        if (!first || !last) return;

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }

      // Fermeture avec échappe
      if (e.key === "Escape") {
        e.preventDefault();
        onCloseRef.current();
      }
    };

    document.addEventListener("keydown", trap);
    // Focus sans scroll en haut
    first?.focus({ preventScroll: true });

    // Bloquer le scroll du body quand la dialog est ouverte
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", trap);
      document.body.style.overflow = "";
    };
  }, [show]);

  if (!show) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm transition-opacity duration-300"
    >
      <div
        ref={dialogRef}
        className={clsx(
          "rounded-lg shadow-xl w-full p-6 mx-4 transform transition-all duration-300",
          bgColor,
          txtColor,
          closing ? "animate-dialog-out" : "animate-dialog-in"
        )}
        style={{
          maxWidth: `${width}rem`,
          maxHeight: "90vh",
        }}
      >
        {title &&
          <Title type="h2" className='mb-6'>{title}</Title>
        }
        <div className="overflow-y-auto max-h-[60vh] md:max-h-[70vh]">
          {children ?
            children :
            (
              <p className="text-sm mb-4">{message}</p>
            )
          }
        </div>
	      {!customBtn &&
        <div className='flex justify-end gap-2 mt-6'>
          <Button
            onClick={onClose}
            variant={secondaryVariant}
          >
            {noConfirmBtn ? "Fermer" : "Annuler"}
          </Button>
          {!noConfirmBtn &&
            <Button
              onClick={() => {
                onConfirm?.();
                onClose();
              }}
              variant={confirmVariant}
            >
              {confirmLabel}
            </Button>
          }
        </div>
	      }
      </div>
    </div>
  );
};
